import { Accordion, ExpandedIndex, Flex, Text } from '@chakra-ui/react';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import CanopySelect from '../../canopy-ui-components/components/CanopySelect/CanopySelect';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
import MenuGroup from './MenuGroup';

export const MENU_MAPPING_TYPES = [
    {
        name: 'menu-navbar',
        translationKey: 'ui.mm.page-title-',
        title: 'menu-navbar',
        viewOn: ['manager', 'micro-fe'],
    },
    {
        name: 'menu-sidebar',
        translationKey: 'ui.mm.side-bar-',
        title: 'menu-sidebar',
        viewOn: ['manager'],
    },
    {
        name: 'menu-appbar',
        translationKey: 'ui.mm.app-bar-',
        title: 'menu-appbar',
        viewOn: ['micro-fe'],
    },
];
export const APPLICATIONS = ['manager', 'micro-fe'];

type MenuMappingProps = {
    t: any;
    showAppDropDown?: boolean;
    setApp?: any;
    app?: any;
    menuStructure?: any;
    allMenus?: any;
    edit?: boolean;
    totalDepth?: number;
    types: any;
    disabled?: boolean;
    onMenuDetailChange?: (data: any) => void;
};
export type MenuMappingSelector = {
    collapseAll: () => void;
};

const MenuMapping = forwardRef<MenuMappingSelector, MenuMappingProps>(
    (
        {
            t,
            showAppDropDown = false,
            setApp,
            app,
            menuStructure,
            allMenus,
            edit = false,
            totalDepth,
            types,
            disabled,
            onMenuDetailChange,
        }: MenuMappingProps,
        ref
    ) => {
        const currentColors = useCurrentColors();
        const getTypes = () => {
            return types?.reduce((res: any, type: any) => {
                res[type.name] = [];
                return res;
            }, {});
        };
        const menuTypes = getTypes();
        const [menus, setMenus] = useState(menuTypes);
        const [expanded, setExpanded] = useState<number[]>([]);
        useImperativeHandle(ref, () => ({
            collapseAll() {
                setExpanded([]);
            },
        }));

        const onChange = useCallback(
            (type: string, menu: any) => {
                if (menus[type]) {
                    menus[type] = menu;
                    setMenus(menus);
                    onMenuDetailChange && onMenuDetailChange(menus);
                }
            },
            [menus]
        );

        const expandOnAdd = useCallback(
            (index: number) => {
                if (!expanded.includes(index)) {
                    setExpanded([...expanded, index]);
                }
            },
            [expanded]
        );

        const accordionChange = useCallback(
            (data: ExpandedIndex) => {
                if (data instanceof Array) {
                    setExpanded(data);
                }
            },
            [expanded]
        );

        const applicationOptions = useMemo(
            () =>
                APPLICATIONS.map((value: string) => ({
                    value,
                    label: t(`app.${value}`),
                })),
            []
        );

        const selectedApplicationOption = useMemo(
            () => applicationOptions.find((type: any) => type.value === app),
            [app]
        );

        const onAppSelection = (selectedVal: any) => {
            setApp(selectedVal?.value);
        };

        useEffect(() => {
            setMenus(getTypes());
        }, [app]);

        useEffect(() => {
            if (menuStructure && menuStructure?.[0]?.app_id) {
                setApp(menuStructure?.[0]?.app_id);
            }
        }, [edit]);

        const menuHandler = (menus: any, app: string) => {
            return menus?.find((menu: any) => menu.app_id === app)?.menus;
        };

        if (!allMenus?.length && edit) {
            return null;
        }

        if (!menuStructure?.length && !edit) {
            return null;
        }

        return (
            <>
                <Flex
                    alignSelf="flex-start"
                    bg={currentColors.componentBg}
                    w="100%"
                    direction="column"
                    p={showAppDropDown ? 0 : 4}
                    mb={4}
                >
                    {showAppDropDown && (
                        <>
                            <Text as="b">
                                {t('ui.menu-mapping.app-dd-note')}
                            </Text>
                            <CanopySelect
                                id="applicationType"
                                height="45px"
                                isClearable={false}
                                placeholder={t('ui.mm.menu-mapping.select-app')}
                                defaultValue={app}
                                value={selectedApplicationOption}
                                options={applicationOptions}
                                getOptionLabel={(option: any) => option.label}
                                getOptionValue={(option: any) => option.value}
                                onChange={onAppSelection}
                                color={currentColors.brand['10']}
                                isDisabled={disabled}
                            ></CanopySelect>
                        </>
                    )}
                    <Accordion
                        index={expanded}
                        allowToggle={true}
                        onChange={accordionChange}
                        width="100%"
                        allowMultiple
                    >
                        {types?.map(
                            (type: Record<string, string>, index: number) => {
                                return (
                                    <MenuGroup
                                        index={index}
                                        key={type.name}
                                        app={app}
                                        edit={edit}
                                        totalDepth={totalDepth}
                                        allMenus={allMenus}
                                        menuStructure={
                                            edit === true
                                                ? menuHandler(
                                                      menuStructure,
                                                      app
                                                  )
                                                : menuStructure
                                        }
                                        title={type.title}
                                        menuKey={type.name}
                                        titleTranslation={type.translationKey}
                                        t={t}
                                        change={onChange}
                                        disabled={disabled}
                                        expandOnAdd={expandOnAdd}
                                    />
                                );
                            }
                        )}
                    </Accordion>
                </Flex>
            </>
        );
    }
);

export default MenuMapping;
